import React, { RefObject } from 'react';
import Picker from '@emoji-mart/react';
import { init } from 'emoji-mart';
import Participants from './Participants';
import {
  formatTimestamp,
  formatUnixTime,
  getColorForStringHex,
  getDefaultPicture,
} from '../../utils';
import { UserMenu } from '../UserMenu/UserMenu';
import { Socket } from 'socket.io-client';
import firebase from 'firebase/compat/app';
import classes from './Chat.module.css';
import { CSSTransition } from 'react-transition-group';
import { getJwtToken } from '../../utils/auth';
import ReactQuill from 'react-quill';
import 'quill-mention';
import 'quill-mention/dist/quill.mention.css';
import Userprofile from '../Userprofile/Userprofile';

export type QuillMentionUserListType = {
  id: string;
  value: string;
};

interface ChatProps {
  chat: ChatMessage[];
  nameMap: StringDict;
  pictureMap: StringDict;
  socket: Socket;
  scrollTimestamp: number;
  getMediaDisplayName: (input: string) => string;
  hide?: boolean;
  isChatDisabled?: boolean;
  user: firebase.User | undefined;
  owner: string | undefined;
  ref: RefObject<Chat>;
  isLiveHls: boolean;
  roomId: string;
  participants: User[];
  coHosts: string[];
  allJoinedParticipants: { uid: string; name: string }[];
  handleChatLikeClick: (index: number, subIndex: number) => void;
}

export class Chat extends React.Component<ChatProps> {
  public state = {
    showTempMessage: true,
    replyMessage: {} as ChatMessage & { originalUuid: string },
    id: '',
    activeTab: 'chat',
    chatMsg: '',
    isNearBottom: true,
    isPickerOpen: false,
    reactionMenu: {
      isOpen: false,
      selectedMsgId: '',
      selectedMsgTimestamp: '',
      yPosition: 0,
      xPosition: 0,
    },
    isViewReplyShowMore: false,
    viewReplyShowMoreId: '',
    isViewSubReplyShowMore: false,
    viewSubReplyShowMoreId: '',
    openMentionNotification: false,
    taggedUsersIdList: [],
  };
  messagesRef = React.createRef<HTMLDivElement>();
  quillRef = React.createRef<ReactQuill>();
  quillWrapperRef = React.createRef<HTMLDivElement>();

  scrollToInput = () => {
    setTimeout(() => {
      this.quillWrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  };

  async componentDidMount() {
    this.scrollToBottom();
    this.messagesRef.current?.addEventListener('scroll', this.onScroll);
    init({});

    const quill = this.quillRef.current?.getEditor();
    if (!quill) return;

    quill.on('text-change', () => {
      const contents = quill.getContents();
      const mentions: any[] = [];

      contents.ops?.forEach((op) => {
        if (op.insert && typeof op.insert === 'object' && op.insert.mention) {
          mentions.push(op.insert.mention);
        }
      });

      this.setState({ taggedUsersIdList: mentions });
    });
  }

  componentDidUpdate(prevProps: ChatProps) {
    if (this.props.scrollTimestamp !== prevProps.scrollTimestamp) {
      if (prevProps.scrollTimestamp === 0 || this.state.isNearBottom) {
        this.scrollToBottom();
      }
    }
    if (this.props.hide !== prevProps.hide) {
      this.scrollToBottom();
    }
  }

  setReactionMenu = (
    isOpen: boolean,
    selectedMsgId?: string,
    selectedMsgTimestamp?: string,
    yPosition?: number,
    xPosition?: number
  ) => {
    this.setState({
      reactionMenu: {
        isOpen,
        selectedMsgId,
        selectedMsgTimestamp,
        yPosition,
        xPosition,
      },
    });
  };

  handleReactionClick = (value: string, id?: string, timestamp?: string) => {
    const msg = this.props.chat.find(
      (m) => m.id === id && m.timestamp === timestamp
    );
    const data = {
      value,
      msgId: id || this.state.reactionMenu.selectedMsgId,
      msgTimestamp: timestamp || this.state.reactionMenu.selectedMsgTimestamp,
    };
    if (
      this.props.socket.id &&
      msg?.reactions?.[value]?.includes(this.props.socket.id)
    ) {
      this.props.socket.emit('CMD:removeReaction', data);
    } else {
      this.props.socket.emit('CMD:addReaction', data);
    }
  };

  updateChatMsg = (_e: any, data: { value: string }) => {
    // console.log(e.target.selectionStart);
    this.setState({ chatMsg: data.value });
  };

  trimEmptyParagraphs = (html: string) => {
    const container = document.createElement('div');
    container.innerHTML = html;

    const isEmptyParagraph = (el: Element) =>
      el.tagName === 'P' &&
      (el.innerHTML === '<br>' || el.innerHTML.trim() === '');

    // Trim start
    while (
      container.firstElementChild &&
      isEmptyParagraph(container.firstElementChild)
    ) {
      container.removeChild(container.firstElementChild);
    }

    // Trim end
    while (
      container.lastElementChild &&
      isEmptyParagraph(container.lastElementChild)
    ) {
      container.removeChild(container.lastElementChild);
    }

    return container.innerHTML;
  };

  checkIfReplyOrMessage = () => {
    if (this.state.replyMessage.uuid) {
      this.sendMsgReply();
    } else {
      this.sendChatMsg();
    }
    requestAnimationFrame(() => {
      this.scrollToInput();
    });
  };

  sendChatMsg = () => {
    if (!this.state.chatMsg) {
      return;
    }
    if (this.quillRef.current?.getEditor().getText().trim().length === 0)
      return;
    if (this.chatTooLong()) {
      return;
    }
    this.setState({ chatMsg: '' });
    this.quillRef.current!.getEditor().setText('');

    const taggedUsers = this.state.taggedUsersIdList.map((i: any) => {
      return { uid: i.id };
    });
    const trimmedMessage = this.trimEmptyParagraphs(this.state.chatMsg);
    if (!trimmedMessage) return;
    this.props.socket.emit('CMD:chatMsg', trimmedMessage, taggedUsers);
  };

  sendMsgReply = () => {
    if (!this.state.replyMessage) {
      return;
    }
    if (this.quillRef.current?.getEditor().getText().trim().length === 0)
      return;
    if (this.chatTooLong()) {
      return;
    }
    this.setState({ chatMsg: '', replyMessage: {} as ChatMessage });
    this.quillRef.current!.getEditor().setText('');
    const taggedUsers = this.state.taggedUsersIdList.map((i: any) => {
      return { uid: i.id };
    });
    this.props.socket.emit('CMD:chatMsgsReply', {
      parentUuid: this.state.replyMessage.uuid,
      msg: this.state.chatMsg.trim(),
      taggedUsers,
    });
  };

  chatTooLong = () => {
    return Boolean(this.state.chatMsg?.length > 10000);
  };

  onScroll = () => {
    this.setState({ isNearBottom: this.isChatNearBottom() });
  };

  isChatNearBottom = () => {
    return (
      this.messagesRef.current &&
      this.messagesRef.current.scrollHeight -
        this.messagesRef.current.scrollTop -
        this.messagesRef.current.offsetHeight <
        100
    );
  };

  scrollToBottom = () => {
    if (this.messagesRef.current) {
      this.messagesRef.current.scrollTop =
        this.messagesRef.current.scrollHeight;
    }
  };

  isMessageAutoGenerated = (cmd: string | undefined): boolean => {
    if (
      cmd === 'host' ||
      cmd === 'CMD:userJoin' ||
      cmd === 'CMD:leaveVideo' ||
      cmd === 'playlistAdd' ||
      cmd === 'seek' ||
      cmd === 'play' ||
      cmd === 'pause' ||
      cmd === 'playbackRate' ||
      cmd === 'lock' ||
      cmd === 'unlock' ||
      cmd === 'vBrowserTimeout' ||
      cmd === 'vBrowserAlmostTimeout'
    ) {
      return true;
    }
    return false;
  };

  formatMessage = (cmd: string, msg: string): React.ReactNode | string => {
    if (cmd === 'host') {
      return (
        <React.Fragment>
          {`changed the video to `}
          <span className="break-all">
            {this.props.getMediaDisplayName(msg)}
          </span>
        </React.Fragment>
      );
    } else if (cmd === 'CMD:userJoin') {
      return (
        <React.Fragment>
          <span style={{ textTransform: 'initial' }}>
            Joined party
            <span className="break-all">
              {this.props.getMediaDisplayName(msg)}
            </span>
          </span>
        </React.Fragment>
      );
    } else if (cmd === 'CMD:leaveVideo') {
      return (
        <React.Fragment>
          <span style={{ textTransform: 'initial' }}>
            Left party
            <span className="break-all">
              {this.props.getMediaDisplayName(msg)}
            </span>
          </span>
        </React.Fragment>
      );
    } else if (cmd === 'playlistAdd') {
      return (
        <React.Fragment>
          {`added to the playlist: `}
          <span className="break-all">
            {this.props.getMediaDisplayName(msg)}
          </span>
        </React.Fragment>
      );
    } else if (cmd === 'seek') {
      return `jumped to ${
        this.props.isLiveHls ? formatUnixTime(msg) : formatTimestamp(msg)
      }`;
    } else if (cmd === 'play') {
      return `started the video at ${formatTimestamp(msg)}`;
    } else if (cmd === 'pause') {
      return `paused the video at ${formatTimestamp(msg)}`;
    } else if (cmd === 'playbackRate') {
      return `set the playback rate to ${msg === '0' ? 'auto' : `${msg}x`}`;
    } else if (cmd === 'lock') {
      return `locked the room`;
    } else if (cmd === 'unlock') {
      return 'unlocked the room';
    } else if (cmd === 'vBrowserTimeout') {
      return (
        <React.Fragment>
          The VBrowser shut down automatically.
          <br />
          Subscribe for longer sessions.
        </React.Fragment>
      );
    } else if (cmd === 'vBrowserAlmostTimeout') {
      return (
        <React.Fragment>
          The VBrowser will shut down soon.
          <br />
          Subscribe for longer sessions.
        </React.Fragment>
      );
    }
    return cmd;
  };

  addEmoji = (emoji: any) => {
    this.setState({ chatMsg: this.state.chatMsg + emoji.native });
    const quill = this.quillRef.current!.getEditor();
    const length = quill.getLength(); // includes trailing '\n'
    quill.insertText(length - 1, emoji.native);
  };
  options = [{ key: 'Everyone', value: 'Everyone', text: 'Everyone' }];

  handleSelectReplyMessage = (message: ChatMessage | null) => {
    this.setState({ replyMessage: message });
  };

  getUniqueUsersUids = () => {
    const uniqueUids: { [key: string]: number } = {};
    this.props.participants.forEach((uid) => {
      if (uid.id && !uniqueUids[uid.id]) {
        uniqueUids[uid.id] = 1;
      }
    });
    return Object.keys(uniqueUids);
  };

  handleMentionUser(searchTerm: any, renderList: any) {
    const allParticipants: { id: string; value: string }[] =
      this.props.allJoinedParticipants?.map((i) => {
        return { id: i.uid, value: i.name };
      });

    let matches;
    if (searchTerm.length === 0) {
      matches = allParticipants;
    } else {
      matches = allParticipants.filter((user) =>
        user.value.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    renderList(matches);
  }

  modules = {
    toolbar: false,
    mention: {
      allowedChars: /^[A-Za-z0-9_ -]*$/, // note: removed `@` from here
      mentionDenotationChars: ['@'], // triggers dropdown on '@'
      source: (searchTerm: any, renderList: any) =>
        this.handleMentionUser(searchTerm, renderList),
    },
    clipboard: {
      matchVisual: false,
    },
  };

  formats = [
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'link',
    'color',
    'background',
    'align',
    'mention',
  ];

  scrollMessageIntoView = (id: string) => {
    const message = document.getElementById(id);
    if (message) {
      message.scrollIntoView({ behavior: 'smooth' });
    }
  };

  isMobile = (): boolean => {
    if (typeof window === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  render() {
    return (
      <div
        className={`flex-col gap-3 mt-6 ${this.props.hide ? 'hidden' : 'flex'}`}
      >
        {this.state.showTempMessage && (
          <div className="flex items-center justify-between gap-5 bg-[#3D026C] p-3 rounded-lg">
            <div className="flex items-center gap-2">
              <i className="ri-information-2-line text-white"></i>
              <span className="text-sm max-sm:text-[10px] text-[#E0ECFFCC]/80">
                Messages can only be seen by people in the party and are deleted
                when the party ends.
              </span>
            </div>
            <button
              onClick={() => {
                this.setState({ showTempMessage: false });
              }}
            >
              <i className="ri-close-line text-white"></i>
            </button>
          </div>
        )}

        {this.state.activeTab === 'chat' && (
          <div>
            <div
              className={`overflow-auto scroll-5 ${
                this.state.showTempMessage
                  ? 'h-[calc(100dvh-275px)] max-lg:h-[calc(100dvh-205px)]'
                  : 'h-[calc(100dvh-210px)] max-lg:h-[calc(100dvh-135px)]'
              } relative`}
              ref={this.messagesRef}
            >
              {this.props.chat.map((msg) => (
                <ChatMessage
                  scrollMessageIntoView={this.scrollMessageIntoView}
                  replyMessage={this.state.replyMessage}
                  handleSelectReplyMessage={this.handleSelectReplyMessage}
                  isMessageAutoGenerated={this.isMessageAutoGenerated}
                  key={msg.timestamp + msg.id}
                  className={
                    msg.id === this.state.reactionMenu.selectedMsgId &&
                    msg.timestamp ===
                      this.state.reactionMenu.selectedMsgTimestamp
                      ? classes.selected
                      : ''
                  }
                  message={msg}
                  pictureMap={this.props.pictureMap}
                  nameMap={this.props.nameMap}
                  formatMessage={this.formatMessage}
                  owner={this.props.owner}
                  user={this.props.user}
                  socket={this.props.socket}
                  isChatDisabled={this.props.isChatDisabled}
                  setReactionMenu={this.setReactionMenu}
                  handleReactionClick={this.handleReactionClick}
                  roomId={this.props.roomId}
                  coHosts={this.props.coHosts}
                  isViewReplyShowMore={this.state.isViewReplyShowMore}
                  viewReplyShowMoreId={this.state.viewReplyShowMoreId}
                  handleUpdateViewMoreReply={(
                    selectedId: string,
                    isOpen: boolean
                  ) => {
                    this.setState({
                      isViewReplyShowMore: isOpen,
                      viewReplyShowMoreId: selectedId,
                    });
                  }}
                  isViewSubReplyShowMore={this.state.isViewSubReplyShowMore}
                  viewSubReplyShowMoreId={this.state.viewSubReplyShowMoreId}
                  handleUpdateViewMoreSubReply={(
                    selectedId: string,
                    isOpen: boolean
                  ) => {
                    this.setState({
                      isViewSubReplyShowMore: isOpen,
                      viewSubReplyShowMoreId: selectedId,
                    });
                  }}
                  handleChatLikeClick={this.props.handleChatLikeClick}
                />
              ))}
              {/* <div ref={this.messagesEndRef} /> */}
              {!this.state.isNearBottom && (
                <div className="sticky bottom-2 flex justify-center z-50">
                  <button
                    className="gradient-btn text-black text-[16px] px-3 py-2 rounded-lg"
                    onClick={this.scrollToBottom}
                  >
                    Jump to bottom
                  </button>
                </div>
              )}
            </div>
            {this.state.isPickerOpen && (
              <div className="absolute bottom-24 z-[999]">
                <Picker
                  theme="dark"
                  previewPosition="none"
                  maxFrequentRows={1}
                  onEmojiSelect={this.addEmoji}
                  onClickOutside={() => this.setState({ isPickerOpen: false })}
                />
              </div>
            )}
            <CSSTransition
              in={this.state.reactionMenu.isOpen}
              timeout={300}
              classNames={{
                enter: classes['reactionMenu-enter'],
                enterActive: classes['reactionMenu-enter-active'],
                exit: classes['reactionMenu-exit'],
                exitActive: classes['reactionMenu-exit-active'],
              }}
              unmountOnExit
            >
              <div
                style={{
                  top: Math.min(
                    this.state.reactionMenu.yPosition - 150,
                    window.innerHeight - 450
                  ),
                  left: this.state.reactionMenu.xPosition - 240,
                }}
                className="fixed"
              >
                <Picker
                  theme="dark"
                  previewPosition="none"
                  maxFrequentRows={1}
                  perLine={6}
                  onClickOutside={() => this.setReactionMenu(false)}
                  onEmojiSelect={(emoji: any) => {
                    this.handleReactionClick(emoji.native);
                    this.setReactionMenu(false);
                  }}
                />
              </div>
            </CSSTransition>

            <div className="relative">
              {this.state.replyMessage?.id && (
                <div className="absolute bottom-full w-full z-50 bg-[#343434] p-2">
                  <div className="bg-[#280647] p-2 text-white rounded border-l-2 border-l-[#4de5fc]">
                    <div className="flex items-center justify-between mb-1">
                      <i className="ri-corner-up-left-double-line text-[20px] text-gray-400"></i>
                      <button
                        onClick={() =>
                          this.handleSelectReplyMessage({} as ChatMessage)
                        }
                      >
                        <i className="ri-close-line text-[20px] text-white"></i>
                      </button>
                    </div>
                    <p className="opacity-80 text-sm line-clamp-3 wrap-anywhere">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: this.state.replyMessage.msg,
                        }}
                      ></div>
                    </p>
                  </div>
                </div>
              )}

              <div className="gradient-modal rounded-[0_0_6px_6px] grid grid-cols-[1fr_60px] max-md:grid-cols-[1fr_30px] items-center">
                <div
                  ref={this.quillWrapperRef}
                  onClick={this.scrollToInput}
                  onTouchStart={this.scrollToInput}
                >
                  <ReactQuill
                    ref={this.quillRef}
                    theme="snow"
                    modules={this.modules}
                    formats={this.formats}
                    placeholder="Here's the thing..."
                    className="w-full text-white text-[16px]"
                    value={this.state.chatMsg}
                    onChange={(val) => {
                      this.updateChatMsg(val, { value: val });
                    }}
                    onKeyDown={(e) => {
                      if (e.shiftKey && e.key === 'Enter') return;
                      if (e.key === 'Enter' && !this.isMobile()) {
                        if (
                          this.quillRef.current!.getEditor().getText().trim()
                            .length !== 0
                        ) {
                          this.checkIfReplyOrMessage();
                        }
                      }
                    }}
                  />
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  <button
                    className="max-md:hidden"
                    onClick={() => {
                      // Add a delay to prevent the click from triggering onClickOutside
                      const curr = this.state.isPickerOpen;
                      setTimeout(
                        () => this.setState({ isPickerOpen: !curr }),
                        100
                      );
                    }}
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.70228 14.5847C8.56653 14.5847 8.43634 14.5299 8.34035 14.4322C8.24436 14.3345 8.19043 14.202 8.19043 14.0639C8.19043 13.9257 8.24436 13.7932 8.34035 13.6955C8.43634 13.5978 8.56653 13.543 8.70228 13.543C8.83803 13.543 8.96823 13.5978 9.06422 13.6955C9.16021 13.7932 9.21414 13.9257 9.21414 14.0639C9.21414 14.202 9.16021 14.3345 9.06422 14.4322C8.96823 14.5299 8.83803 14.5847 8.70228 14.5847ZM15.8682 14.5847C15.7325 14.5847 15.6023 14.5299 15.5063 14.4322C15.4103 14.3345 15.3564 14.202 15.3564 14.0639C15.3564 13.9257 15.4103 13.7932 15.5063 13.6955C15.6023 13.5978 15.7325 13.543 15.8682 13.543C16.004 13.543 16.1342 13.5978 16.2302 13.6955C16.3262 13.7932 16.3801 13.9257 16.3801 14.0639C16.3801 14.202 16.3262 14.3345 16.2302 14.4322C16.1342 14.5299 16.004 14.5847 15.8682 14.5847Z"
                        fill="#fff"
                        stroke="#fff"
                        strokeWidth="2.09043"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.2375 18.7523H14.3323M22.522 12.5017C22.522 18.2554 17.9388 22.9194 12.2849 22.9194C6.63099 22.9194 2.04785 18.2554 2.04785 12.5017C2.04785 6.748 6.63099 2.08398 12.2849 2.08398C17.9388 2.08398 22.522 6.748 22.522 12.5017Z"
                        stroke="#fff"
                        strokeWidth="2.09043"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={this.checkIfReplyOrMessage}
                    className="text-white text-2xl p-1"
                  >
                    <i className="ri-send-plane-2-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {this.state.activeTab === 'participants' && (
          <Participants
            owner={this.props.owner}
            roomId={this.props.roomId}
            socket={this.props.socket}
            nameMap={this.props.nameMap}
            pictureMap={this.props.pictureMap}
            user={this.props.user}
            uniqueUids={this.getUniqueUsersUids()}
          />
        )}
      </div>
    );
  }
}

const ChatMessage = ({
  message,
  nameMap,
  pictureMap,
  formatMessage,
  user,
  socket,
  owner,
  roomId,
  handleSelectReplyMessage,
  isMessageAutoGenerated,
  coHosts,
  isViewReplyShowMore,
  viewReplyShowMoreId,
  handleUpdateViewMoreReply,
  isViewSubReplyShowMore,
  viewSubReplyShowMoreId,
  handleUpdateViewMoreSubReply,
  handleChatLikeClick,
}: {
  scrollMessageIntoView: (id: string) => void;
  isMessageAutoGenerated: (cmd: string | undefined) => boolean;
  replyMessage: ChatMessage | null;
  message: ChatMessage;
  nameMap: StringDict;
  pictureMap: StringDict;
  formatMessage: (cmd: string, msg: string) => React.ReactNode;
  user: firebase.User | undefined;
  socket: Socket;
  owner: string | undefined;
  isChatDisabled: boolean | undefined;
  setReactionMenu: (
    isOpen: boolean,
    selectedMsgId?: string,
    selectedMsgTimestamp?: string,
    yPosition?: number,
    xPosition?: number
  ) => void;
  handleReactionClick: (value: string, id?: string, timestamp?: string) => void;
  className: string;
  roomId: string;
  handleSelectReplyMessage: (message: ChatMessage | null) => void;
  coHosts: string[];
  isViewReplyShowMore: boolean;
  viewReplyShowMoreId: string;
  handleUpdateViewMoreReply: (selectedId: string, isOpen: boolean) => void;
  isViewSubReplyShowMore: boolean;
  viewSubReplyShowMoreId: string;
  handleUpdateViewMoreSubReply: (selectedId: string, isOpen: boolean) => void;
  handleChatLikeClick: (index: number, subIndex: number) => void;
}) => {
  const {
    id,
    timestamp,
    cmd,
    msg,
    system,
    videoTS,
    user: chatUser,
    uuid,
  } = message;

  return (
    <div className="mb-3">
      <div className="relative overflow-hidden p-[1px] after:content-[''] after:absolute after:inset-0 after:rounded-xl after:p-[5px] after:pointer-events-none after:z-0 gradient-pink-border">
        <div
          className={`relative z-[1] overflow-hidden bg-[#040404] flex gap-2 p-3 rounded-xl`}
        >
          {id ? (
            // <Userprofile />
            <div
              className="mainProfileBox tenMember relative flex-shrink-0"
              title="WatchParty Plus subscriber"
            >
              <div className="size-[46px] p-px rounded-full relative z-[5]">
                <img
                  // className={isSub ? classes.subscriber : ''}
                  className="userProfile size-full rounded-full border-2 border-green-500 object-cover"
                  src={
                    pictureMap[id] ||
                    getDefaultPicture(nameMap[id], getColorForStringHex(id))
                  }
                  alt="userAvatar"
                />
                <div className="size-5 absolute -right-[2px] -top-[2px] after:content-['10K'] after:absolute after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:text-[6px] after:font-['Poppins'] after:font-bold after:flex after:items-center after:justify-center after:bg-white after:rounded-full after:px-[2px] after:text-black after:h-[9px]">
                  <svg
                    fill="#27ae60"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M256 0c4.6 0 9.2 1 13.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0z" />
                  </svg>
                </div>
              </div>
            </div>
          ) : null}

          <h3
            className="text-white text-[16px] pt-[5px] w-full"
            id={message.uuid ? message.uuid : timestamp}
          >
            <span className="text-[#00D97E] me-1 font-['Poppins'] capitalize">
              {Boolean(system) && 'System'}
              {nameMap[id] || id}
            </span>
            {isMessageAutoGenerated(cmd) ? (
              <>
                {!cmd && msg} {cmd && formatMessage(cmd, msg)}
              </>
            ) : (
              <div
                className="font-['Poppins']"
                dangerouslySetInnerHTML={{
                  __html: msg,
                }}
              ></div>
            )}
          </h3>
        </div>
      </div>
      <div className="flex items-center justify-between p-[2px]">
        <div className="flex items-center gap-2">
          {!isMessageAutoGenerated(message.cmd) && (
            <>
              <span className="text-white">{message.likesCount ?? 0}</span>
              {message.isLike ? (
                <button
                  className=""
                  onClick={() => {
                    const data = {
                      uid: user?.uid,
                      uuid: message.uuid,
                      token: getJwtToken(),
                    };
                    socket.emit('CMD:removeReaction', data);
                    handleChatLikeClick(-1, -1);
                  }}
                >
                  <i className="ri-heart-3-fill text-red-500 text-2xl"></i>
                </button>
              ) : (
                <button
                  className=""
                  onClick={() => {
                    const data = {
                      uid: user?.uid,
                      uuid: message.uuid,
                      token: getJwtToken(),
                    };
                    socket.emit('CMD:addReaction', data);
                    handleChatLikeClick(-1, -1);
                  }}
                >
                  <i className="ri-heart-3-line text-red-500 text-2xl"></i>
                </button>
              )}
              <button
                className="text-white text-sm"
                onClick={() => {
                  const sendMsgInput =
                    document.getElementById('send-msg-input');
                  if (sendMsgInput) {
                    sendMsgInput.focus();
                  }
                  handleSelectReplyMessage(message);
                }}
              >
                Reply
              </button>
            </>
          )}
          <h5
            className="text-[#8E8E93] text-[12px]"
            title={new Date(timestamp).toLocaleDateString()}
          >
            {new Date(timestamp).toLocaleTimeString()}
            {Boolean(videoTS) && ' @ '}
            {formatTimestamp(videoTS)}
          </h5>
        </div>
        {!isMessageAutoGenerated(message.cmd) && (
          <UserMenu
            displayName={nameMap[id] || id}
            user={user}
            timestamp={timestamp}
            socket={socket}
            userToManage={chatUser?.uid || id}
            deleteMsgId={uuid}
            disabled={
              isMessageAutoGenerated(message.cmd) ||
              (user?.uid !== chatUser?.uid &&
                owner !== user?.uid &&
                !Boolean(
                  coHosts.length > 0 && coHosts.includes(user?.uid ?? '')
                ))
            }
            // disabled={!Boolean(owner && owner === user?.uid)}
            // !Boolean(coHosts.length > 0 && coHosts.includes(user?.uid ?? ''))
            trigger={
              <button
                className={`text-[22px] text-[#B5B5BE] ${
                  user?.uid !== chatUser?.uid &&
                  owner !== user?.uid &&
                  !Boolean(
                    coHosts.length > 0 && coHosts.includes(user?.uid ?? '')
                  )
                    ? 'hidden'
                    : 'block'
                }`}
              >
                <svg
                  width="22"
                  height="6"
                  viewBox="0 0 22 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.75535 0.802154C4.0565 0.802154 5.11128 1.8538 5.11128 3.15108C5.11128 4.44835 4.0565 5.5 2.75535 5.5C1.4542 5.5 0.399414 4.44835 0.399414 3.15108C0.399414 1.8538 1.4542 0.802154 2.75535 0.802154ZM11.001 0.801955C12.3022 0.801955 13.357 1.8536 13.357 3.15088C13.357 4.44815 12.3022 5.4998 11.001 5.4998C9.69987 5.4998 8.64508 4.44815 8.64508 3.15088C8.64508 1.8536 9.69987 0.801955 11.001 0.801955ZM21.6016 3.15088C21.6016 1.8536 20.5468 0.801955 19.2456 0.801955C17.9445 0.801955 16.8897 1.8536 16.8897 3.15088C16.8897 4.44815 17.9445 5.4998 19.2456 5.4998C20.5468 5.4998 21.6016 4.44815 21.6016 3.15088Z"
                    fill="#828282"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.75535 0.802154C4.0565 0.802154 5.11128 1.8538 5.11128 3.15108C5.11128 4.44835 4.0565 5.5 2.75535 5.5C1.4542 5.5 0.399414 4.44835 0.399414 3.15108C0.399414 1.8538 1.4542 0.802154 2.75535 0.802154ZM11.001 0.801955C12.3022 0.801955 13.357 1.8536 13.357 3.15088C13.357 4.44815 12.3022 5.4998 11.001 5.4998C9.69987 5.4998 8.64508 4.44815 8.64508 3.15088C8.64508 1.8536 9.69987 0.801955 11.001 0.801955ZM21.6016 3.15088C21.6016 1.8536 20.5468 0.801955 19.2456 0.801955C17.9445 0.801955 16.8897 1.8536 16.8897 3.15088C16.8897 4.44815 17.9445 5.4998 19.2456 5.4998C20.5468 5.4998 21.6016 4.44815 21.6016 3.15088Z"
                    fill="black"
                    fill-opacity="0.2"
                  />
                </svg>
              </button>
            }
            roomId={roomId}
            owner={owner}
            chatUserId={chatUser?.uid ?? ''}
          />
        )}
      </div>
      {message?.replies && message.replies.length > 0 && (
        <div className="text-white">
          {message.replies
            .slice(
              0,
              isViewReplyShowMore && viewReplyShowMoreId === uuid
                ? message.replies.length
                : 1
            )
            .map((msgReplyItem, msgReplyIndex) => {
              return (
                <div className="mb-3 ml-8">
                  <div className="relative overflow-hidden p-[1px] after:content-[''] after:absolute after:inset-0 after:rounded-xl after:p-[5px] after:pointer-events-none after:z-0 gradient-pink-border">
                    <div
                      className={`relative z-[1] overflow-hidden bg-[#040404] flex gap-2 p-3 rounded-xl`}
                    >
                      <div
                        className="mainProfileBox tenMember relative flex-shrink-0"
                        title="WatchParty Plus subscriber"
                      >
                        <div className="size-[46px] p-px rounded-full relative z-[5]">
                          <img
                            className="userProfile size-full rounded-full border-2 border-green-500 object-cover"
                            src={
                              pictureMap[msgReplyItem.id] ||
                              getDefaultPicture(
                                nameMap[msgReplyItem.id],
                                getColorForStringHex(msgReplyItem.id)
                              )
                            }
                            alt="userAvatar"
                          />
                          <div className="size-5 absolute -right-[2px] -top-[2px] after:content-['10K'] after:absolute after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:text-[6px] after:font-['Poppins'] after:font-bold after:flex after:items-center after:justify-center after:bg-white after:rounded-full after:px-[2px] after:text-black after:h-[9px]">
                            <svg
                              fill="#27ae60"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 512 512"
                            >
                              <path d="M256 0c4.6 0 9.2 1 13.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <h3
                        id={message.uuid ? message.uuid : timestamp}
                        className="text-white text-[16px] pt-[5px]"
                      >
                        <span className="text-[#00D97E] me-1 font-['Poppins'] capitalize">
                          {nameMap[msgReplyItem.id] || msgReplyItem.id}
                        </span>
                        <div
                          className="wrap-anywhere font-['Poppins']"
                          dangerouslySetInnerHTML={{
                            __html: msgReplyItem.msg,
                          }}
                        ></div>
                      </h3>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-[2px]">
                    <div className="flex items-center gap-2">
                      {!isMessageAutoGenerated(message.cmd) && (
                        <>
                          <span className="text-white">
                            {msgReplyItem.likesCount ?? 0}
                          </span>
                          {msgReplyItem.isLike ? (
                            <button
                              className=""
                              onClick={() => {
                                const data = {
                                  uid: user?.uid,
                                  uuid: msgReplyItem.uuid,
                                  token: getJwtToken(),
                                };
                                socket.emit('CMD:removeReaction', data);
                                handleChatLikeClick(msgReplyIndex, -1);
                              }}
                            >
                              <i className="ri-heart-3-fill text-red-500 text-2xl"></i>
                            </button>
                          ) : (
                            <button
                              className=""
                              onClick={() => {
                                const data = {
                                  uid: user?.uid,
                                  uuid: msgReplyItem.uuid,
                                  token: getJwtToken(),
                                };
                                socket.emit('CMD:addReaction', data);
                                handleChatLikeClick(msgReplyIndex, -1);
                              }}
                            >
                              <i className="ri-heart-3-line text-red-500 text-2xl"></i>
                            </button>
                          )}
                          <button
                            className="text-white text-sm"
                            onClick={() => {
                              const sendMsgInput =
                                document.getElementById('send-msg-input');
                              if (sendMsgInput) {
                                sendMsgInput.focus();
                              }
                              handleSelectReplyMessage(msgReplyItem);
                            }}
                          >
                            Reply
                          </button>
                        </>
                      )}
                      <h5
                        className="text-[#8E8E93] text-[12px]"
                        title={new Date(timestamp).toLocaleDateString()}
                      >
                        {new Date(timestamp).toLocaleTimeString()}
                        {Boolean(videoTS) && ' @ '}
                        {formatTimestamp(videoTS)}
                      </h5>
                    </div>
                    <UserMenu
                      displayName={nameMap[id] || id}
                      user={user}
                      timestamp={timestamp}
                      socket={socket}
                      userToManage={msgReplyItem?.user?.uid || id}
                      deleteMsgId={msgReplyItem.uuid}
                      disabled={
                        isMessageAutoGenerated(message.cmd) ||
                        (user?.uid !== msgReplyItem?.user?.uid &&
                          owner !== user?.uid &&
                          !Boolean(
                            coHosts.length > 0 &&
                              coHosts.includes(user?.uid ?? '')
                          ))
                      }
                      // disabled={!Boolean(owner && owner === user?.uid)}
                      // !Boolean(coHosts.length > 0 && coHosts.includes(user?.uid ?? ''))
                      trigger={
                        <button
                          className={`text-[22px] text-[#B5B5BE] ${
                            user?.uid !== msgReplyItem?.user?.uid &&
                            owner !== user?.uid &&
                            !Boolean(
                              coHosts.length > 0 &&
                                coHosts.includes(user?.uid ?? '')
                            )
                              ? 'hidden'
                              : 'block'
                          }`}
                        >
                          <svg
                            width="22"
                            height="6"
                            viewBox="0 0 22 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M2.75535 0.802154C4.0565 0.802154 5.11128 1.8538 5.11128 3.15108C5.11128 4.44835 4.0565 5.5 2.75535 5.5C1.4542 5.5 0.399414 4.44835 0.399414 3.15108C0.399414 1.8538 1.4542 0.802154 2.75535 0.802154ZM11.001 0.801955C12.3022 0.801955 13.357 1.8536 13.357 3.15088C13.357 4.44815 12.3022 5.4998 11.001 5.4998C9.69987 5.4998 8.64508 4.44815 8.64508 3.15088C8.64508 1.8536 9.69987 0.801955 11.001 0.801955ZM21.6016 3.15088C21.6016 1.8536 20.5468 0.801955 19.2456 0.801955C17.9445 0.801955 16.8897 1.8536 16.8897 3.15088C16.8897 4.44815 17.9445 5.4998 19.2456 5.4998C20.5468 5.4998 21.6016 4.44815 21.6016 3.15088Z"
                              fill="#828282"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M2.75535 0.802154C4.0565 0.802154 5.11128 1.8538 5.11128 3.15108C5.11128 4.44835 4.0565 5.5 2.75535 5.5C1.4542 5.5 0.399414 4.44835 0.399414 3.15108C0.399414 1.8538 1.4542 0.802154 2.75535 0.802154ZM11.001 0.801955C12.3022 0.801955 13.357 1.8536 13.357 3.15088C13.357 4.44815 12.3022 5.4998 11.001 5.4998C9.69987 5.4998 8.64508 4.44815 8.64508 3.15088C8.64508 1.8536 9.69987 0.801955 11.001 0.801955ZM21.6016 3.15088C21.6016 1.8536 20.5468 0.801955 19.2456 0.801955C17.9445 0.801955 16.8897 1.8536 16.8897 3.15088C16.8897 4.44815 17.9445 5.4998 19.2456 5.4998C20.5468 5.4998 21.6016 4.44815 21.6016 3.15088Z"
                              fill="black"
                              fill-opacity="0.2"
                            />
                          </svg>
                        </button>
                      }
                      roomId={roomId}
                      owner={owner}
                      chatUserId={msgReplyItem?.user?.uid ?? ''}
                    />
                  </div>
                  {msgReplyIndex === 0 &&
                    message?.replies &&
                    message.replies.length > 1 && (
                      <button
                        className="mb-1 text-[16px] text-gray-400"
                        onClick={() => {
                          handleUpdateViewMoreReply(
                            uuid,
                            isViewReplyShowMore &&
                              viewReplyShowMoreId === message.uuid
                              ? false
                              : true
                          );
                        }}
                      >
                        View{' '}
                        {isViewReplyShowMore &&
                        viewReplyShowMoreId === message.uuid
                          ? 'less'
                          : 'more'}{' '}
                        replies{' '}
                        {!(
                          isViewReplyShowMore &&
                          viewReplyShowMoreId === message.uuid
                        ) &&
                          '(' +
                            (message?.replies && message?.replies?.length - 1) +
                            ')'}
                      </button>
                    )}
                  {msgReplyItem?.replies && msgReplyItem.replies.length > 0 && (
                    <div className="text-white">
                      {msgReplyItem.replies
                        .slice(
                          0,
                          isViewSubReplyShowMore &&
                            viewSubReplyShowMoreId === msgReplyItem.uuid
                            ? msgReplyItem.replies.length
                            : 1
                        )
                        .map((msgSubReplyItem, msgSubReplyIndex) => {
                          return (
                            <div className="mb-3 ml-8">
                              <div className="relative overflow-hidden p-[1px] after:content-[''] after:absolute after:inset-0 after:rounded-xl after:p-[5px] after:pointer-events-none after:z-0 gradient-pink-border">
                                <div
                                  className={`relative z-[1] overflow-hidden bg-[#040404] flex gap-2 p-3 rounded-xl`}
                                >
                                  <div
                                    className="mainProfileBox tenMember relative flex-shrink-0"
                                    title="WatchParty Plus subscriber"
                                  >
                                    <div className="size-[46px] p-px rounded-full relative z-[5]">
                                      <img
                                        // className={isSub ? classes.subscriber : ''}
                                        className="userProfile size-full rounded-full border-2 border-green-500 object-cover"
                                        src={
                                          pictureMap[msgSubReplyItem.id] ||
                                          getDefaultPicture(
                                            nameMap[msgSubReplyItem.id],
                                            getColorForStringHex(
                                              msgSubReplyItem.id
                                            )
                                          )
                                        }
                                        alt="userAvatar"
                                      />
                                      <div className="size-5 absolute -right-[2px] -top-[2px] after:content-['10K'] after:absolute after:left-1/2 after:top-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:text-[6px] after:font-['Poppins'] after:font-bold after:flex after:items-center after:justify-center after:bg-white after:rounded-full after:px-[2px] after:text-black after:h-[9px]">
                                        <svg
                                          fill="#27ae60"
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 512 512"
                                        >
                                          <path d="M256 0c4.6 0 9.2 1 13.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0z" />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>

                                  <h3
                                    id={
                                      msgSubReplyItem.uuid
                                        ? msgSubReplyItem.uuid
                                        : timestamp
                                    }
                                    className="text-white text-[16px] pt-[5px]"
                                  >
                                    <span className="text-[#00D97E] me-1 font-['Poppins'] capitalize inline-block">
                                      {nameMap[msgSubReplyItem.id] ||
                                        msgSubReplyItem.id}
                                    </span>
                                    <div
                                      className="wrap-anywhere font-['Poppins']"
                                      dangerouslySetInnerHTML={{
                                        __html: msgSubReplyItem.msg,
                                      }}
                                    ></div>
                                  </h3>
                                </div>
                              </div>
                              <div className="flex items-center justify-between p-[2px]">
                                <div className="flex items-center gap-2">
                                  {!isMessageAutoGenerated(message.cmd) && (
                                    <>
                                      {msgSubReplyItem.isLike ? (
                                        <button
                                          className=""
                                          onClick={() => {
                                            const data = {
                                              uid: user?.uid,
                                              uuid: msgSubReplyItem.uuid,
                                              token: getJwtToken(),
                                            };
                                            socket.emit(
                                              'CMD:removeReaction',
                                              data
                                            );
                                            handleChatLikeClick(
                                              -1,
                                              msgSubReplyIndex
                                            );
                                          }}
                                        >
                                          <i className="ri-heart-3-fill text-red-500 text-2xl"></i>
                                        </button>
                                      ) : (
                                        <button
                                          className=""
                                          onClick={() => {
                                            const data = {
                                              uid: user?.uid,
                                              uuid: msgSubReplyItem.uuid,
                                              token: getJwtToken(),
                                            };
                                            socket.emit(
                                              'CMD:addReaction',
                                              data
                                            );
                                            handleChatLikeClick(
                                              -1,
                                              msgSubReplyIndex
                                            );
                                          }}
                                        >
                                          <i className="ri-heart-3-line text-red-500 text-2xl"></i>
                                        </button>
                                      )}
                                    </>
                                  )}
                                  <span className="text-white">
                                    {msgSubReplyItem.likesCount ?? 0}
                                  </span>
                                  <h5
                                    className="text-[#8E8E93] text-[12px]"
                                    title={new Date(
                                      timestamp
                                    ).toLocaleDateString()}
                                  >
                                    {new Date(timestamp).toLocaleTimeString()}
                                    {Boolean(videoTS) && ' @ '}
                                    {formatTimestamp(videoTS)}
                                  </h5>
                                </div>
                                <UserMenu
                                  displayName={nameMap[id] || id}
                                  user={user}
                                  timestamp={timestamp}
                                  socket={socket}
                                  userToManage={
                                    msgSubReplyItem?.user?.uid || id
                                  }
                                  deleteMsgId={msgSubReplyItem.uuid}
                                  disabled={
                                    isMessageAutoGenerated(message.cmd) ||
                                    (user?.uid !== msgSubReplyItem?.user?.uid &&
                                      owner !== user?.uid &&
                                      !Boolean(
                                        coHosts.length > 0 &&
                                          coHosts.includes(user?.uid ?? '')
                                      ))
                                  }
                                  // disabled={!Boolean(owner && owner === user?.uid)}
                                  // !Boolean(coHosts.length > 0 && coHosts.includes(user?.uid ?? ''))
                                  trigger={
                                    <button
                                      className={`text-[22px] text-[#B5B5BE] ${
                                        user?.uid !==
                                          msgSubReplyItem?.user?.uid &&
                                        owner !== user?.uid &&
                                        !Boolean(
                                          coHosts.length > 0 &&
                                            coHosts.includes(user?.uid ?? '')
                                        )
                                          ? 'hidden'
                                          : 'block'
                                      }`}
                                    >
                                      <svg
                                        width="22"
                                        height="6"
                                        viewBox="0 0 22 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          clipRule="evenodd"
                                          d="M2.75535 0.802154C4.0565 0.802154 5.11128 1.8538 5.11128 3.15108C5.11128 4.44835 4.0565 5.5 2.75535 5.5C1.4542 5.5 0.399414 4.44835 0.399414 3.15108C0.399414 1.8538 1.4542 0.802154 2.75535 0.802154ZM11.001 0.801955C12.3022 0.801955 13.357 1.8536 13.357 3.15088C13.357 4.44815 12.3022 5.4998 11.001 5.4998C9.69987 5.4998 8.64508 4.44815 8.64508 3.15088C8.64508 1.8536 9.69987 0.801955 11.001 0.801955ZM21.6016 3.15088C21.6016 1.8536 20.5468 0.801955 19.2456 0.801955C17.9445 0.801955 16.8897 1.8536 16.8897 3.15088C16.8897 4.44815 17.9445 5.4998 19.2456 5.4998C20.5468 5.4998 21.6016 4.44815 21.6016 3.15088Z"
                                          fill="#828282"
                                        />
                                        <path
                                          fillRule="evenodd"
                                          clipRule="evenodd"
                                          d="M2.75535 0.802154C4.0565 0.802154 5.11128 1.8538 5.11128 3.15108C5.11128 4.44835 4.0565 5.5 2.75535 5.5C1.4542 5.5 0.399414 4.44835 0.399414 3.15108C0.399414 1.8538 1.4542 0.802154 2.75535 0.802154ZM11.001 0.801955C12.3022 0.801955 13.357 1.8536 13.357 3.15088C13.357 4.44815 12.3022 5.4998 11.001 5.4998C9.69987 5.4998 8.64508 4.44815 8.64508 3.15088C8.64508 1.8536 9.69987 0.801955 11.001 0.801955ZM21.6016 3.15088C21.6016 1.8536 20.5468 0.801955 19.2456 0.801955C17.9445 0.801955 16.8897 1.8536 16.8897 3.15088C16.8897 4.44815 17.9445 5.4998 19.2456 5.4998C20.5468 5.4998 21.6016 4.44815 21.6016 3.15088Z"
                                          fill="black"
                                          fill-opacity="0.2"
                                        />
                                      </svg>
                                    </button>
                                  }
                                  roomId={roomId}
                                  owner={owner}
                                  chatUserId={msgSubReplyItem?.user?.uid ?? ''}
                                />
                              </div>
                              {msgSubReplyIndex === 0 &&
                                msgReplyItem?.replies &&
                                msgReplyItem.replies.length > 1 && (
                                  <button
                                    className="mb-1 text-[16px] text-gray-400"
                                    onClick={() => {
                                      handleUpdateViewMoreSubReply(
                                        msgReplyItem.uuid,
                                        isViewSubReplyShowMore &&
                                          viewSubReplyShowMoreId ===
                                            msgReplyItem.uuid
                                          ? false
                                          : true
                                      );
                                    }}
                                  >
                                    View{' '}
                                    {isViewSubReplyShowMore &&
                                    viewSubReplyShowMoreId === msgReplyItem.uuid
                                      ? 'less'
                                      : 'more'}{' '}
                                    replies{' '}
                                    {!(
                                      isViewSubReplyShowMore &&
                                      viewSubReplyShowMoreId ===
                                        msgReplyItem.uuid
                                    ) &&
                                      '(' +
                                        (msgReplyItem.replies &&
                                          msgReplyItem.replies.length - 1) +
                                        ')'}
                                  </button>
                                )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
